import { markdownToClipboardContent } from "./markdown";

function withNativeNewlines(text) {
  const normalized = String(text ?? "").replace(/\r\n/g, "\n");
  if (typeof navigator !== "undefined" && /windows/i.test(navigator.userAgent)) {
    return normalized.replace(/\n/g, "\r\n");
  }
  return normalized;
}

function createClipboardItem(plain, htmlDoc) {
  const plainBlob = new Blob([plain], { type: "text/plain" });
  const htmlBlob = new Blob([htmlDoc], { type: "text/html" });

  try {
    return new ClipboardItem({
      "text/plain": plainBlob,
      "text/html": htmlBlob,
    });
  } catch {
    return new ClipboardItem({
      "text/plain": Promise.resolve(plainBlob),
      "text/html": Promise.resolve(htmlBlob),
    });
  }
}

function copyHtmlViaExecCommand(htmlFragment, plain) {
  if (typeof document === "undefined") return false;

  const container = document.createElement("div");
  container.setAttribute("contenteditable", "true");
  container.innerHTML = htmlFragment;
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  document.body.appendChild(container);

  const selection = window.getSelection();
  const previousRanges = [];
  if (selection) {
    for (let i = 0; i < selection.rangeCount; i++) {
      previousRanges.push(selection.getRangeAt(i));
    }
    selection.removeAllRanges();
  }

  try {
    const range = document.createRange();
    range.selectNodeContents(container);
    selection?.addRange(range);
    const copied = document.execCommand("copy");
    if (copied) return true;
  } catch {
    // Fall through to plain-text copy.
  } finally {
    selection?.removeAllRanges();
    previousRanges.forEach((range) => selection?.addRange(range));
    document.body.removeChild(container);
  }

  return copyPlainViaExecCommand(plain);
}

function copyPlainViaExecCommand(plain) {
  if (typeof document === "undefined") return false;

  const textArea = document.createElement("textarea");
  textArea.value = plain;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "-9999px";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);

  try {
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

export async function copyTextToClipboard(text, html) {
  const plain = withNativeNewlines(String(text ?? ""));
  if (!plain.trim()) return false;

  const htmlDoc = html ? String(html) : "";
  const htmlFragment = htmlDoc.includes("<!--StartFragment-->")
    ? htmlDoc.split("<!--StartFragment-->")[1]?.split("<!--EndFragment-->")[0] ||
      htmlDoc
    : htmlDoc;

  if (
    htmlDoc &&
    typeof navigator !== "undefined" &&
    navigator.clipboard?.write &&
    typeof ClipboardItem !== "undefined"
  ) {
    try {
      await navigator.clipboard.write([createClipboardItem(plain, htmlDoc)]);
      return true;
    } catch {
      // Fallback below handles browsers that reject text/html writes.
    }
  }

  if (htmlFragment) {
    const copiedRich = copyHtmlViaExecCommand(htmlFragment, plain);
    if (copiedRich) return true;
  }

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(plain);
      return true;
    } catch {
      // Fallback below handles browsers/environments where async clipboard is blocked.
    }
  }

  return copyPlainViaExecCommand(plain);
}

export async function copyMarkdownToClipboard(markdown) {
  const source = String(markdown ?? "").trim();
  if (!source) return false;

  let html;
  let plain = source;
  try {
    const content = markdownToClipboardContent(source);
    html = content.html || undefined;
    if (content.plain?.trim()) {
      plain = content.plain;
    }
  } catch {
    html = undefined;
  }

  return copyTextToClipboard(plain, html);
}
